import { z } from "zod";
import { parseBrand, type Brand } from "./brand";
import { THEME_KEY_PATTERN } from "./theme";
import { blockSchema, serviceTimesBlockSchema, type Block } from "./blocks";
import {
  parseEach,
  publicSermonSchema,
  publicSermonSeriesSchema,
  type PublicSermon,
  type PublicSermonSeries,
} from "./sermons";
import { publicDevotionalSchema, type PublicDevotional } from "./devotionals";
import { parseMemberSignIn, parsePublicProducts, type PublicProduct } from "./shop";
import type { ChurchSignInAddresses } from "@how/tenancy";

/**
 * What `get_public_page` and `get_preview_page` return (ADR 0031): the church, its published menus, and a page. The
 * envelope is parsed strictly; blocks are parsed one at a time, so a block that does not match its contract costs that
 * block and a log line rather than the whole page.
 */

export interface NavigationItem {
  readonly label: string;
  readonly href: string;
  readonly children?: readonly NavigationItem[] | undefined;
  /**
   * A top-level header item the church marked as one of its two actions (ADR 0073, the section row): `primary` is a
   * filled button ("Plan your visit"), `secondary` an outlined one ("Find a location"). A theme with a section row
   * draws them there; every other theme draws them as ordinary links.
   */
  readonly action?: MenuAction | undefined;
}

/** The two kinds of menu button a church may mark (private.check_navigation_items). */
export const MENU_ACTIONS = ["primary", "secondary"] as const;
export type MenuAction = (typeof MENU_ACTIONS)[number];

const navigationItemSchema: z.ZodType<NavigationItem> = z.lazy(() =>
  z.object({
    label: z.string().min(1).max(80),
    href: z.string().min(1).max(500),
    children: z.array(navigationItemSchema).max(12).optional(),
    // An unknown mark is ignored rather than failing the whole menu: the item is drawn as a plain link.
    action: z.enum(MENU_ACTIONS).optional().catch(undefined),
  }),
);

const storedPageSchema = z.object({
  slug: z.string().min(1).max(120),
  path: z.string().min(1).max(121),
  title: z.string().min(1).max(160),
  description: z.string().max(300).optional(),
  status: z.enum(["draft", "published"]),
  seo: z
    .object({
      title: z.string().max(70).optional(),
      description: z.string().max(300).optional(),
      noindex: z.boolean().default(false),
      image: z
        .object({
          path: z.string().min(1),
          alt: z.string().min(1),
          width: z.number().int().positive().optional(),
          height: z.number().int().positive().optional(),
        })
        .optional(),
    })
    .default({ noindex: false }),
  blocks: z.array(z.unknown()).max(40),
});

/** A form as a visitor's browser needs it: what to draw, and the token minted when the page was rendered. */
const absentable = (schema: z.ZodString) =>
  schema
    .nullish()
    .transform((value) => value ?? undefined)
    .optional();

export const publicFormSchema = z.object({
  name: z.string().min(1),
  description: absentable(z.string()),
  thank_you_message: absentable(z.string()),
  token: z.string().min(1),
  fields: z
    .array(
      z.object({
        key: z.string().min(1),
        label: z.string().min(1),
        type: z.enum(["text", "long_text", "email", "phone", "choice", "checkbox", "consent"]),
        required: z.boolean().default(false),
        options: z.array(z.string()).max(40).optional(),
        channel: z.string().optional(),
        help: z.string().optional(),
      }),
    )
    .max(30),
});

export type PublicForm = z.infer<typeof publicFormSchema>;

/**
 * A campus as the public site reads it (ADR 0036): what a church prints about its own buildings. Service times use
 * the same shape as the service_times block's entries, so the block can draw from the table without translation.
 * `timeZone` is absent when the campus keeps the church's zone.
 */
export const publicCampusSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  slug: z.string().min(1),
  addressLines: z.array(z.string()).max(4).default([]),
  phone: z.string().optional(),
  mapUrl: z.url({ protocol: /^https$/ }).optional(),
  timeZone: z.string().optional(),
  serviceTimes: z.array(serviceTimesBlockSchema.shape.services.element).max(12).default([]),
  /** The campus page's own text (Milestone 5 B1): plain paragraphs, as the church wrote them. */
  description: z.string().max(2000).optional(),
  /** In the location chooser, or reachable by its link only (Church Center's "listed"). Older envelopes: listed. */
  isListed: z.boolean().default(true),
});

export type PublicCampus = z.infer<typeof publicCampusSchema>;

/**
 * A group as the website shows it (ADR 0040, task 5): a public, active group with when and where it meets, who
 * leads it (adults, by name, never a child), whether it is full, and how one joins. Nothing else about anyone.
 */
export const publicGroupSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  slug: z.string().min(1),
  description: z.string().optional(),
  category: z.string().optional(),
  categoryId: z.string().optional(),
  campus: z.object({ name: z.string().min(1), slug: z.string().min(1) }).optional(),
  meetingDay: z
    .enum(["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"])
    .optional(),
  /** "19:00": a wall-clock time at the campus. */
  meetingTime: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
    .optional(),
  meetingLocation: z.string().optional(),
  joiningRule: z.enum(["open", "request", "closed"]),
  isFull: z.boolean().default(false),
  ageMin: z.number().int().optional(),
  ageMax: z.number().int().optional(),
  leaders: z.array(z.string()).default([]),
});

export type PublicGroup = z.infer<typeof publicGroupSchema>;

/**
 * An event as the website shows it (ADR 0041, task 8): a public, active event with its upcoming dates, where it
 * is, whether a date is full, and how one registers. Nobody is named.
 */
export const publicEventSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  slug: z.string().min(1),
  description: z.string().optional(),
  ministry: z.string().optional(),
  location: z.string().optional(),
  category: z.string().optional(),
  categoryId: z.string().optional(),
  campus: z
    .object({ name: z.string().min(1), slug: z.string().min(1), timeZone: z.string().optional() })
    .optional(),
  registration: z.enum(["none", "open", "closed"]),
  /** A paid event's price per person, and its currency (ADR 0076); absent for a free event. */
  priceCents: z.number().int().positive().optional(),
  currency: z
    .string()
    .regex(/^[a-z]{3}$/)
    .optional(),
  /** The add-ons a guest may choose with a paid place (ADR 0047 step 3, ADR 0076). */
  addOns: z
    .array(
      z.object({
        id: z.string().min(1),
        name: z.string().min(1),
        description: z.string().optional(),
        variants: z
          .array(
            z.object({
              id: z.string().min(1),
              name: z.string().min(1),
              priceCents: z.number().int().nonnegative(),
              available: z.boolean(),
            }),
          )
          .min(1),
      }),
    )
    .optional(),
  occurrences: z
    .array(
      z.object({
        id: z.string().min(1),
        startsAt: z.string().min(1),
        endsAt: z.string().min(1),
        note: z.string().optional(),
        isFull: z.boolean().default(false),
      }),
    )
    .default([]),
});

export type PublicEvent = z.infer<typeof publicEventSchema>;

const envelopeSchema = z.object({
  site: z.object({
    name: z.string().min(1),
    slug: z.string().min(1),
    locale: z.string().min(1).optional(),
    /** The church's own zone; a campus without one keeps it. */
    timeZone: z.string().min(1).optional(),
  }),
  /** Active campuses in the church's order. Parsed one at a time, so a bad campus costs that campus, not the site. */
  campuses: z.array(z.unknown()).default([]),
  /** The church's public, active groups. Parsed one at a time, like campuses. */
  groups: z.array(z.unknown()).default([]),
  /** The church's public, active events with upcoming dates. Parsed one at a time. */
  events: z.array(z.unknown()).default([]),
  /**
   * Published sermons a page's sermon feature blocks draw, and the church's series that have one (ADR 0061). Parsed
   * one at a time; empty for a church without sermons_media.
   */
  sermons: z.array(z.unknown()).default([]),
  sermonSeries: z.array(z.unknown()).default([]),
  /** The latest visible devotionals a page's devotional block draws (ADR 0077). Parsed one at a time. */
  devotionals: z.array(z.unknown()).default([]),
  /**
   * The products a page's store collection blocks draw, or the Shop page lists (ADR 0071), and where Buy goes. Parsed
   * one at a time; empty for a church without commerce or not selling through HoW's store.
   */
  products: z.array(z.unknown()).default([]),
  memberSignIn: z.unknown().optional(),
  navigation: z.object({
    header: z.array(navigationItemSchema).default([]),
    footer: z.array(navigationItemSchema).default([]),
  }),
  /*
   * Whatever the church stored, passed through as-is and made sense of by `parseBrand`, which falls back per value.
   * A brand that is wrong should cost a church its accent colour, never its whole website.
   */
  brand: z.unknown().default({}),
  page: storedPageSchema.nullable(),
  /** Keyed by form id. A block pointing at a form that is gone or closed finds nothing here. */
  forms: z.record(z.string(), z.unknown()).default({}),
  /*
   * Present only on a theme preview (ADR 0050 section 2): the theme the home page is drawn in for a staff member who
   * is choosing one. The theme comes from the preview token's row in the database, never from the address. A marker
   * that does not parse is dropped rather than costing the page.
   */
  themePreview: z.unknown().optional(),
  /*
   * Present on `get_public_page_window` only (ADR 0056): when the database read the page and for how far ahead its
   * blocks and window edges hold, so the public site knows how long its in-memory copy may serve.
   */
  delivery: z
    .object({ at: z.string().min(1), horizonSeconds: z.number().int().positive() })
    .optional(),
});

const themePreviewSchema = z.object({
  key: z.string().regex(THEME_KEY_PATTERN),
  name: z.string().min(1).max(60),
});

export type ThemePreview = z.infer<typeof themePreviewSchema>;

export interface DeliveryWindow {
  readonly at: string;
  readonly horizonSeconds: number;
}

/**
 * A block's window as a staff preview receives it (ADR 0031, amendment of 2026-10-03): when it shows to visitors, and
 * whether it is hidden from them at the moment the preview was drawn. A visitor's page never carries one: the database
 * leaves out a block outside its window and strips the schedule from the rest.
 */
export const blockScheduleSchema = z.object({
  showFrom: z.string().min(1).optional(),
  showUntil: z.string().min(1).optional(),
  hiddenNow: z.boolean().default(false),
});

export type BlockSchedule = z.infer<typeof blockScheduleSchema>;

export type StoredPage = Omit<z.infer<typeof storedPageSchema>, "blocks"> & {
  readonly blocks: readonly Block[];
  /**
   * Each block's schedule, index for index with `blocks`; null for a block with none, which is every block on a
   * visitor's page. The block contracts strip unknown keys, so the schedule is read beside them rather than inside.
   */
  readonly schedules: readonly (BlockSchedule | null)[];
};

export interface RejectedBlock {
  readonly index: number;
  readonly type: string;
  readonly issues: readonly string[];
}

export interface PublicSite {
  readonly site: z.infer<typeof envelopeSchema>["site"];
  readonly navigation: z.infer<typeof envelopeSchema>["navigation"];
  readonly forms: Readonly<Record<string, PublicForm>>;
  /** The church's active campuses, in its order. Empty for a church whose envelope predates ADR 0036. */
  readonly campuses: readonly PublicCampus[];
  /** The church's public groups, by name. Empty until the groups module is entitled (ADR 0040). */
  readonly groups: readonly PublicGroup[];
  /** The church's public events with upcoming dates. Empty until the events module is entitled (ADR 0041). */
  readonly events: readonly PublicEvent[];
  /** Published sermons the page's blocks draw (ADR 0061). Empty until sermons_media is entitled. */
  readonly sermons: readonly PublicSermon[];
  readonly sermonSeries: readonly PublicSermonSeries[];
  /** The latest visible devotionals the page's devotional block draws (ADR 0077). Empty without the module. */
  readonly devotionals: readonly PublicDevotional[];
  /** Products on sale in HoW's store, for a store collection block or the Shop page (ADR 0071). Usually empty. */
  readonly products: readonly PublicProduct[];
  /** Where the Buy link goes (ADR 0067 R9): the church's own member address only when it prefers it. */
  readonly memberSignIn: ChurchSignInAddresses;
  readonly brand: Brand;
  readonly page: StoredPage | null;
  /** The theme being previewed, on a theme preview only; null on every page a visitor sees. */
  readonly themePreview: ThemePreview | null;
  /** When the database read a delivery window and how far ahead it holds (ADR 0056); null on every other read. */
  readonly delivery: DeliveryWindow | null;
  /** Blocks left out because they did not match their contract. The caller logs them. */
  readonly rejected: readonly RejectedBlock[];
}

/** Parses the envelope, then each block and each form on its own. Throws only when the envelope itself is malformed. */
export function parsePublicSite(input: unknown): PublicSite {
  const envelope = envelopeSchema.parse(input);
  const brand = parseBrand(envelope.brand);
  const preview = themePreviewSchema.safeParse(envelope.themePreview);
  const themePreview = preview.success ? preview.data : null;
  const delivery = envelope.delivery ?? null;
  const forms: Record<string, PublicForm> = {};
  const rejected: RejectedBlock[] = [];
  for (const [id, raw] of Object.entries(envelope.forms)) {
    const parsed = publicFormSchema.safeParse(raw);
    if (parsed.success) forms[id] = parsed.data;
    else {
      rejected.push({
        index: -1,
        type: `form ${id}`,
        issues: parsed.error.issues.map((issue) => `${issue.path.join(".")}: ${issue.message}`),
      });
    }
  }
  // A campus that does not match its contract is left out and reported, like a block; the others still render.
  const campuses: PublicCampus[] = [];
  envelope.campuses.forEach((raw, index) => {
    const parsed = publicCampusSchema.safeParse(raw);
    if (parsed.success) campuses.push(parsed.data);
    else {
      rejected.push({
        index,
        type: "campus",
        issues: parsed.error.issues.map((issue) => `${issue.path.join(".")}: ${issue.message}`),
      });
    }
  });
  const groups: PublicGroup[] = [];
  envelope.groups.forEach((raw, index) => {
    const parsed = publicGroupSchema.safeParse(raw);
    if (parsed.success) groups.push(parsed.data);
    else {
      rejected.push({
        index,
        type: "group",
        issues: parsed.error.issues.map((issue) => `${issue.path.join(".")}: ${issue.message}`),
      });
    }
  });
  const events: PublicEvent[] = [];
  envelope.events.forEach((raw, index) => {
    const parsed = publicEventSchema.safeParse(raw);
    if (parsed.success) events.push(parsed.data);
    else {
      rejected.push({
        index,
        type: "event",
        issues: parsed.error.issues.map((issue) => `${issue.path.join(".")}: ${issue.message}`),
      });
    }
  });
  const sermons = parseEach(publicSermonSchema, envelope.sermons);
  const sermonSeries = parseEach(publicSermonSeriesSchema, envelope.sermonSeries);
  const devotionals = parseEach(publicDevotionalSchema, envelope.devotionals);
  const products = parsePublicProducts(envelope.products);
  const memberSignIn = parseMemberSignIn(envelope.memberSignIn);
  if (envelope.page === null)
    return {
      ...envelope,
      brand,
      themePreview,
      delivery,
      forms,
      campuses,
      groups,
      events,
      sermons,
      sermonSeries,
      devotionals,
      products,
      memberSignIn,
      page: null,
      rejected,
    };
  const blocks: Block[] = [];
  const schedules: (BlockSchedule | null)[] = [];
  envelope.page.blocks.forEach((raw, index) => {
    const parsed = blockSchema.safeParse(raw);
    if (parsed.success) {
      blocks.push(parsed.data);
      schedules.push(blockSchedule(raw));
    } else {
      const type =
        typeof raw === "object" && raw !== null && "type" in raw ? String(raw.type) : "unknown";
      rejected.push({
        index,
        type,
        issues: parsed.error.issues.map((issue) => `${issue.path.join(".")}: ${issue.message}`),
      });
    }
  });
  return {
    ...envelope,
    brand,
    themePreview,
    delivery,
    forms,
    campuses,
    groups,
    events,
    sermons,
    sermonSeries,
    devotionals,
    products,
    memberSignIn,
    page: { ...envelope.page, blocks, schedules },
    rejected,
  };
}

/** The schedule a stored block carries, or null. One that does not parse is dropped: the block still draws. */
function blockSchedule(raw: unknown): BlockSchedule | null {
  if (typeof raw !== "object" || raw === null || !("schedule" in raw)) return null;
  const parsed = blockScheduleSchema.safeParse(raw.schedule);
  return parsed.success ? parsed.data : null;
}

/** The public URL of a file in the church's public media area. */
export function publicMediaUrl(supabaseUrl: string, path: string): string {
  const encoded = path.split("/").map(encodeURIComponent).join("/");
  return `${supabaseUrl.replace(/\/$/, "")}/storage/v1/object/public/how-public/${encoded}`;
}

/** A path from the request URL, as the database expects it: no leading or trailing slash; "" is the home page. */
export function requestPath(pathname: string): string {
  return decodeURIComponent(pathname).replace(/^\/+|\/+$/g, "");
}

/**
 * The sentence a page is shared with: its search description, else its own description. The meta description and
 * og:description use the same value, so a link preview says what a search result says.
 */
export function pageDescription(page: Pick<StoredPage, "seo" | "description">): string | undefined {
  return page.seo.description ?? page.description;
}

/** The Open Graph type: a church's home page is the site itself; every other page is an article on it. */
export function openGraphType(page: Pick<StoredPage, "slug">): "website" | "article" {
  return page.slug === "home" ? "website" : "article";
}

/**
 * A page's own address on the church's host, for og:url and the canonical link. Built from the page's path rather
 * than the request, so a link shared with ?form=…&sent=1 or ?preview=… still points at the page itself. Church sites
 * are served over HTTPS only.
 */
export function canonicalUrl(hostname: string, page: Pick<StoredPage, "path">): string {
  return `https://${hostname}${page.path}`;
}
